import type { DevicePowerSchedule, IdleBehavior } from './powerSchedule.js';
// =============================================================================
// Shared domain types for CornerStone.
// Mirrors HANDOFF.md §4 (data model) and CALIBRATION_UX.md §6.1 (state model).
// =============================================================================

export type Point = { x: number; y: number };

/** Additional local mesh correction layered on top of the 4-corner homography. */
export type FineWarpPoint = {
  id: string;
  side: WallSide;
  /** Source-space anchor UV captured before the handle is dragged. */
  source: Point;
  /** Destination-space target point in global normalized [0,1]. */
  dest: Point;
  /** Influence radius in global normalized destination space. */
  radius: number;
};

/** Horizontal slice for the slice-style warp. A band boundary on ONE face
 *  (left/right of the seam). Its two endpoints (seam side + outer side) are
 *  dragged vertically and independent, so the line may tilt. The renderer
 *  builds a per-column piecewise-linear dest-y → source-y remap from the
 *  sorted slices of a side. Separate channel from {@link FineWarpPoint}.
 *  @deprecated v2로 대체 예정 — seam 전용이라 Calibration v2에서 폐기. */
export type WarpSlice = {
  id: string;
  side: WallSide;
  /** Source-space y (0..1) this band samples from — captured at creation. */
  sourceY: number;
  /** Destination y (0..1) at the seam-side endpoint (dragged vertically). */
  seamDestY: number;
  /** Destination y (0..1) at the outer-edge endpoint (dragged vertically). */
  outerDestY: number;
};

/**
 * Polygon mask (NBL 46 — 캘리 마스킹). A closed polygon in destination-global
 * normalized [0,1] space. The renderer blacks out fragments INSIDE the polygon
 * (e.g. a doorway/pillar to keep dark), or OUTSIDE it when `invert` is true
 * (crop to the polygon). Independent of the warp geometry — applied as a final
 * coverage gate after the source sample, so it composes with any surface model.
 * @deprecated v2로 대체 예정 — {@link SurfaceMask}(베지어 핸들 + 텍스처 래스터)가 대체.
 */
export type MaskPolygon = {
  id: string;
  /** Closed polygon vertices, destination-global [0,1], CW or CCW. ≥3 points. */
  points: Point[];
  /** false (default) = black out inside; true = black out outside (crop). */
  invert?: boolean;
};

/**
 * Bezier surface patch (NBL 78 — 베지어 서피스 워핑). A bicubic Bézier surface
 * defined by a 4×4 grid of control points in destination-global [0,1] space,
 * bound to one wall face. Layered on top of the 4-corner homography like
 * {@link FineWarpPoint}: the renderer evaluates the surface to compute a smooth
 * destination→destination displacement, letting an operator bow a flat plane
 * into a curved surface. A patch whose control grid is the identity (evenly
 * spaced 0..1) produces zero displacement, so an empty/identity patch list is a
 * no-op and byte-compatible with pre-78 presets.
 *
 * `controlPoints` is row-major: index = row*4 + col, row 0 = top (y≈0),
 * col 0 = left (x≈0). Exactly 16 points.
 * @deprecated v2로 대체 예정 — {@link MeshWarp} 그리드 세분화가 대체.
 */
export type BezierPatch = {
  id: string;
  side: WallSide;
  /** 16 control points, row-major 4×4. Destination-global [0,1]. */
  controlPoints: Point[];
};

/** Order: TL, TR, BR, BL (clockwise). Global normalized [0,1] — see HANDOFF §4.1. */
export type Corners = readonly [Point, Point, Point, Point];

/** row-major 3x3 homography matrix */
export type Matrix3x3 = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

export type CornerIndex = 0 | 1 | 2 | 3;
/** @deprecated v2로 대체 예정 — Calibration v2(Surface 모델)에는 좌/우 벽 개념이 없다. */
export type WallSide = 'left' | 'right';

// -----------------------------------------------------------------------------
// Source split (HANDOFF §4.2, CALIBRATION_UX §2.8)
// -----------------------------------------------------------------------------

/** @deprecated v2로 대체 예정 — source split은 Calibration v2에서 폐기. */
export type SplitLockMode = 'center' | 'parallel' | 'free';

/** @deprecated v2로 대체 예정 — source split은 Calibration v2에서 폐기. */
export type SplitLine = {
  /** X coordinate of the split seam at the top edge (y=0). Range [0,1]. */
  topX: number;
  /** X coordinate of the split seam at the bottom edge (y=1). Range [0,1]. */
  bottomX: number;
  lockMode: SplitLockMode;
};

// -----------------------------------------------------------------------------
// Calibration UI state (CALIBRATION_UX §6.1)
// -----------------------------------------------------------------------------

export type EditMode = 'symmetric' | 'free';

export type TestPattern =
  | 'none'
  | 'checker'
  | 'grid'
  | 'crosshair'
  | 'numbered'
  | 'outline'
  | 'split-indicator';

export type PatternMode = 'replace' | 'overlay' | 'split';

export type CoordUnit = 'normalized' | 'pixel';

export type NudgeStep = 1 | 10 | 0.1;

export type Heatmap = 'off' | 'on';

export type Selection =
  | { kind: 'corner'; wall: WallSide; index: CornerIndex }
  | { kind: 'split'; position: 'top' | 'bottom' }
  | { kind: 'fineWarp'; id: string }
  | { kind: 'warpSlice'; id: string; end: 'seam' | 'outer' }
  | null;

// -----------------------------------------------------------------------------
// Playback source (CALIBRATION_UX §2.9)
// -----------------------------------------------------------------------------

export type PlaybackSourceKind =
  | 'playlist'
  | 'single-media'
  | 'single-notice'
  | 'test-procedural'
  | 'test-uploaded';

export type ProceduralPattern =
  | 'checker'
  | 'grid'
  | 'solid-white'
  | 'solid-gray'
  | 'solid-black';

export type PlaybackExecution = { id: string; cycle: number };

export type PlaybackSource = (
  | { kind: 'playlist'; playlistId?: string }
  | { kind: 'single-media'; mediaId: string }
  | { kind: 'single-notice'; noticeId: string }
  | { kind: 'test-procedural'; pattern: ProceduralPattern }
  | { kind: 'test-uploaded'; mediaId: string }
) & { execution?: PlaybackExecution; idle?: boolean };

export type MediaType = 'video' | 'image' | 'lottie' | 'anamorphic' | 'pattern';

/** Optional for legacy organizations; enforced group APIs attach these fields. */
export type ContentAccessMetadata = {
  ownerGroupId?: string | null;
  sharedToAll?: boolean;
  scopeClassified?: boolean;
  revision?: number;
  canWrite?: boolean;
  /** 승인된(송출 중) 편성이 이 콘텐츠를 쓰고 있다 — 플레이리스트 안의 미디어·슬라이드도 포함(SCDS-102). */
  scheduled?: boolean;
  /** 편성 중일 때 어디서 쓰는지 — 플레이리스트면 스케줄 이름, 미디어·슬라이드면 플레이리스트 이름. '편성 중' 말풍선용(SCDS-110). */
  scheduledIn?: string[];
  /** canWrite 가 false 인 이유 중 화면이 그대로 보여 줄 문장(송출 중 잠금). 없으면 보통의 읽기 전용. */
  writeBlockedReason?: string;
};

export type CurrentMedia = {
  filename: string;
  durationSec: number | null;
  positionSec: number;
  type: MediaType;
  isPlaying: boolean;
};

export type RecentMedia = {
  id: string;
  filename: string;
};

// -----------------------------------------------------------------------------
// WarpPreset (HANDOFF §4)
// -----------------------------------------------------------------------------

/** @deprecated v2로 대체 예정 — {@link WarpPresetV2} 사용. */
export type WarpPreset = {
  id: string;
  name: string;
  // Destination
  leftCorners: Corners;
  rightCorners: Corners;
  fineWarpPoints: FineWarpPoint[];
  warpSlices: WarpSlice[];
  /** NBL 46 — polygon masks (blackout/crop). Empty = no masking. */
  maskPolygons: MaskPolygon[];
  /** NBL 78 — bézier surface patches. Empty = flat (homography only). */
  bezierPatches: BezierPatch[];
  // Source split
  splitTopX: number;
  splitBottomX: number;
  splitLockMode: SplitLockMode;
  // Flags
  symmetric: boolean;
  bypassWarp: boolean;
  // Output
  outputResolutionW: number;
  outputResolutionH: number;
  // Lifecycle
  protected: boolean;
  lastSavedAt: string; // ISO
  createdAt: string; // ISO
};

/**
 * Preset payload as it appears inside an export file. Strips id /
 * timestamps / `protected` (regenerated on import) but keeps every field
 * needed to reproduce the warp + source-split state.
 */
export type WarpPresetExportItem = Omit<
  WarpPreset,
  'id' | 'protected' | 'lastSavedAt' | 'createdAt'
>;

/** JSON file written by Export and accepted by Import. */
export type WarpPresetExportFile = {
  /** Bumped when the export shape changes incompatibly. */
  schemaVersion: 1;
  /** App identifier — currently informational, used to surface mismatch warnings. */
  appName: string;
  /** ISO timestamp of when the file was produced. */
  exportedAt: string;
  presets: WarpPresetExportItem[];
};

// -----------------------------------------------------------------------------
// Calibration v2 — Surface model (docs/old/CALIBRATION_V2_POLYGON_PLAN.md §3)
//
// v2 buries the "left/right two-face corner projection" premise: a preset is a
// list of Surfaces, each = corner-pin homography + optional mesh subdivision
// warp (curved installs) + optional free-form mask (clipping shape) + content
// binding. Media plays undistorted and is CLIPPED by the mask — warp(geometry)
// and mask(shape) are independent layers.
// -----------------------------------------------------------------------------

/** 마스크 정점 — 폴리곤 꼭짓점 + 선택적 베지어 핸들(곡선 엣지용, 원 등).
 *  좌표는 정규화 [0,1], Surface 로컬 UV 공간. */
export type MaskVertex = {
  p: Point;
  /** 이 점으로 들어오는(직전 엣지의) 큐빅 베지어 핸들. 없으면 직선. */
  h1?: Point;
  /** 이 점에서 나가는(다음 엣지의) 큐빅 베지어 핸들. 없으면 직선. */
  h2?: Point;
};

export type MaskShapeKind = 'rect' | 'circle' | 'star' | 'diamond' | 'custom';

export type SurfaceMask = {
  /** 프리셋 도형에서 출발했는지 기록 (편집하면 'custom'으로 승격). */
  kind: MaskShapeKind;
  /** 최소 3개, 상한 없음(마스크 텍스처 방식이라 셰이더 제약 없음). */
  vertices: MaskVertex[];
  /** 가장자리 소프트닝 px (0 = 하드 엣지). */
  feather: number;
  /** true면 도형 바깥을 보여줌. */
  invert: boolean;
};

/** surface별 콘텐츠 소스 — 스케줄 엔진은 무수정, 텍스처 바인딩만 surface별. */
export type SurfaceContent =
  | { type: 'schedule' }                  // 디바이스 스케줄 재생물 (기본값, 현행 동일)
  | { type: 'media'; mediaId: string }    // 고정 미디어 루프 (영상/이미지)
  | { type: 'color'; color: string };     // 단색

/** 메시 세분화 warp — 곡면(기둥·아치·굴곡 벽) 대응. 코너핀 위에 얹는 그리드 변위. */
export type MeshWarp = {
  /** 그리드 분할 수 (2~8, UI에서 2×2/4×4/8×8 선택). */
  cols: number;
  rows: number;
  /** (cols+1)×(rows+1) row-major. 최종 destination-global [0,1] 좌표.
   *  메시 생성 시 현재 코너핀 quad를 따라 초기화한다. */
  points: Point[];
};

/** v2 국소 미세 보정점 — v1 {@link FineWarpPoint}에서 `side`만 제거. */
export type FineWarpPointV2 = Omit<FineWarpPoint, 'side'>;

/** 원본 미디어에서 surface가 사용할 정규화 영역. */
export type SurfaceSourceRegion = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Surface에 고정되는 미디어 UV 확대/이동. */
export type SurfaceMediaTransform = {
  /** 1 = 100%, 3 = 300%. */
  scale: number;
  /** 선택 sourceRegion 내부 UV 기준 이동량. */
  offsetX: number;
  offsetY: number;
};

export type Surface = {
  id: string;
  /** 편집 화면 식별 색상. Player 출력에는 사용하지 않는다. */
  color?: string;
  /** 미지정(구 프리셋)이면 원본 전체 영역으로 해석한다. */
  sourceRegion?: SurfaceSourceRegion;
  /** 미지정(구 프리셋)이면 100%, 중앙 정렬. */
  mediaTransform?: SurfaceMediaTransform;
  /** 코너핀 4점 (TL·TR·BR·BL, 기존과 동일). */
  corners: Corners;
  fineWarpPoints: FineWarpPointV2[];
  /** null = 코너핀만 (평면). 굴곡 시 그리드 warp. */
  mesh: MeshWarp | null;
  /** null = 마스크 없음(전체 사각형). */
  mask: SurfaceMask | null;
  /** multi-surface 시 도형별 다른 콘텐츠. */
  content: SurfaceContent;
};

export type WarpPresetV2 = {
  id: string;
  /** 캘리브레이션이 속한 디바이스. 기기별로 완전 격리 — 다른 기기 목록에
   *  나타나지 않고 적용도 해당 기기에만 전달된다. null = 레거시(기기 지정
   *  이전 데이터)로, 어떤 기기 목록에도 노출하지 않는다. */
  deviceId: string | null;
  name: string;
  /** 하나의 미디어 소스를 공유할 수 있는 독립 출력 surface 목록. */
  surfaces: Surface[];
  bypassWarp: boolean;
  outputResolutionW?: number;
  outputResolutionH?: number;
  protected: boolean;
  lastSavedAt: string; // ISO
  createdAt: string; // ISO
};

// -----------------------------------------------------------------------------
// Save status (CALIBRATION_UX §6.7)
// -----------------------------------------------------------------------------

export type SaveStatus = 'saved' | 'unsaved' | 'new';

// -----------------------------------------------------------------------------
// Media library (HANDOFF §4 MediaItem — MEDIA_MANAGEMENT_UX to follow)
// -----------------------------------------------------------------------------

/**
 * 미디어 업로드 최대 용량 (바이트). 타입별 분리 (Figma Toast 스펙 기준).
 * - 이미지: 10MB
 * - 비디오: 2GB (2026-10-01 대교 — 500MB 에서 상향. 기기는 서버가 만든 변환본을 틀어서
 *   재생 안정성은 원본 크기와 무관하고, S3 백업이 해시 중복 제거로 바뀐 뒤 올렸다.
 *   매뉴얼 1.1 은 그래도 웹용(MP4 H.264, 1080p 8Mbps 안팎)으로 내보내 올리길 권한다.)
 *
 * 근거: 이미지 과대 파일은 불필요. 클라이언트 사전 검증 + 서버(단일/청크 finalize) 양쪽에서 강제.
 */
export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;

/** 상한을 안내문에 쓰는 꼴 — "10MB" · "2GB". 화면 토스트·서버 413·매뉴얼이 같은 글자를 쓴다. */
export function formatUploadLimit(bytes: number): string {
  const mb = Math.round(bytes / (1024 * 1024));
  return mb >= 1024 && mb % 1024 === 0 ? `${mb / 1024}GB` : `${mb}MB`;
}

/** 미디어 타입별 업로드 상한. image 외(video/lottie 등)는 비디오 상한 적용. */
export function maxUploadBytesFor(mediaType: MediaType): number {
  return mediaType === 'image' ? MAX_IMAGE_UPLOAD_BYTES : MAX_VIDEO_UPLOAD_BYTES;
}

// -----------------------------------------------------------------------------
// Folder — single-level grouping for library media / playlists / scenes.
// Flat (no nesting); a unified table discriminated by `kind`. Entities point
// at a folder via `folderId` (null = ungrouped / root).
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// AI draft model config (NBL_REQ_605) — platform-scoped, BO-managed. The AI
// draft generator runs behind a provider abstraction; this selects/configures
// the active provider. API keys are stored encrypted server-side and never
// returned to the client (only `hasApiKey` is exposed).
// -----------------------------------------------------------------------------
export type AiProviderKind = 'gemini' | 'ollama' | 'openai';

export type AiModelConfig = {
  provider: AiProviderKind;
  /** Model id (e.g. 'gemini-2.5-flash', 'qwen3:4b', 'gpt-4o-mini'). Empty =
   *  provider default / env. */
  modelId: string;
  /** Base URL for ollama / openai-compatible endpoints. Empty = provider default. */
  baseUrl: string;
  /** Request timeout (ms). */
  timeoutMs: number;
  /** True when an API key is stored (openai). The key itself is never returned. */
  hasApiKey: boolean;
  updatedAt: string | null;
  /**
   * 이 설정이 어디서 왔는지(2026-09-22 고객사별 키). 'org' = 그 고객사 전용 설정,
   * 'platform' = 운영사 기본값을 상속, 'env' = 둘 다 없어 서버 .env(Vertex Gemini).
   * 운영사 기본값 자체를 조회할 때는 생략된다.
   */
  source?: 'org' | 'platform' | 'env';
};

// -----------------------------------------------------------------------------
// EmailConfig — 운영사가 화면에서 정하는 메일 발송 설정(2026-09-22 대교).
// 비밀번호는 서버에만 암호화 저장되고 클라이언트에는 hasPassword 로만 알린다.
// 화면에서 저장한 값이 없으면 서버 .env 값을 그대로 보여 주며 source='env'.
// -----------------------------------------------------------------------------
export type EmailDriverKind = 'console' | 'smtp';
export type EmailConfig = {
  driver: EmailDriverKind;
  host: string;
  port: number;
  user: string;
  /** From 헤더. 비우면 user 주소를 쓴다. */
  from: string;
  hasPassword: boolean;
  updatedAt: string | null;
  source: 'stored' | 'env';
};

/** 운영사 설정 · 네이버 뉴스 API (NAVER API HUB). 키 원문은 돌려주지 않고 등록 여부와 Client ID 끝 4자리만 보여 준다. */
export type NaverNewsConfig = {
  enabled: boolean;
  hasClientId: boolean;
  hasClientSecret: boolean;
  clientIdHint: string | null;
  updatedAt: string | null;
};

// -----------------------------------------------------------------------------
// MemberGroup (NBL_REQ_104) — 부서·팀 그룹. Members assigned by user id.
// -----------------------------------------------------------------------------
export type MemberGroup = {
  id: string;
  name: string;
  memberUserIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type FolderKind = 'media' | 'playlist' | 'scene';

export type Folder = ContentAccessMetadata & {
  id: string;
  name: string;
  kind: FolderKind;
  /** Manual ordering within a kind (ascending). */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type MediaProcessingStatus = 'pending' | 'processing' | 'ready' | 'failed';

/** A device-sized, integrity-checked rendition of a video or image. */
/** 서버 영상 변환 진행(WS media:progress). step 은 1부터, etaSec 은 초반엔 null. */
export type MediaTranscodeProgress = { id: string; percent: number; step: number; steps: number; etaSec: number | null };

export type MediaVariant = {
  /** Pixel height of this rendition (width scales to preserve aspect). */
  height: number;
  /** Relative path under DATA_DIR/media/. */
  relPath: string;
  mimeType?: string;
  sizeBytes?: number;
  sha256?: string;
  codec?: string;
  fps?: number;
  bitrate?: number;
};

export type MediaItem = ContentAccessMetadata & {
  id: string;
  filename: string;
  originalName: string;
  /** Library folder this media belongs to. null/undefined = ungrouped. */
  folderId?: string | null;
  mimeType: string;
  mediaType: MediaType;
  /** Bytes on disk. */
  sizeBytes: number;
  /** Video duration in seconds, null for images/patterns. */
  durationSec: number | null;
  /** Pixel dimensions probed on upload (ffprobe). null for legacy rows not yet
   *  backfilled, or when probing failed. */
  width: number | null;
  height: number | null;
  /** Stored under DATA_DIR/media/, relative path. */
  relPath: string;
  /** Optional thumbnail under DATA_DIR/thumbnails/, relative path. */
  thumbnailRelPath: string | null;
  /** Server-side optimization pipeline state. Images and videos go
   *  pending→processing→ready|failed while the original remains available. */
  processingStatus: MediaProcessingStatus;
  /** Resolution-reduced renditions produced by transcoding, each a
   *  scaled copy under DATA_DIR/media/. Empty until transcode completes / for
   *  non-video. Used to serve a device-profile-appropriate resolution. */
  variants: MediaVariant[];
  /** 관리자 화면 전용 일시 값 — WS media:progress 로만 채워지고 서버 응답에는 없다. */
  transcodeProgress?: MediaTranscodeProgress;
  /** SHA-256 of the file on disk, hex-encoded. null for legacy 0.1.x rows
   *  that haven't been re-hashed yet; the media route lazy-hashes on first
   *  download manifest request. Used by Player Sync Engine for integrity. */
  sha256?: string | null;
  /** Re-measured byte size from the hashing pass. May differ from sizeBytes
   *  if the upload reported a different length (rare). Player Sync Engine
   *  trusts this field over sizeBytes when checking a downloaded blob. */
  sizeBytesVerified?: number | null;
  /** 서버에서 이 항목이 쓰는 용량: 원본 + 변환본(목록 응답에만). 2026-10-12 */
  storageBytes?: number;
  /** 그중 먼저 올라온 다른 항목과 같은 파일(하드링크)을 빼고 이 항목 때문에 더 쓰는 양. 0이면 공유 파일. */
  storageAddedBytes?: number;
  createdAt: string;
  /** Last metadata change (name/folder). Set to createdAt on upload. Drives
   *  the "최근 업데이트순" sort in the library. Optional for legacy rows. */
  updatedAt?: string;
  lastPlayedAt: string | null;
};

// -----------------------------------------------------------------------------
// Playlist (ordered media sequence, loops at end by design)
// -----------------------------------------------------------------------------

export type PlaylistTransitionType = 'none' | 'crossfade' | 'fade-to-black';

/**
 * Fade/Black 전환 시간 정책(ms). Cut(none)은 0(즉시).
 * - 최소 200ms: 그보다 짧으면 incoming 디코드 대기만으로도 cut처럼 보임.
 * - 최대 9999ms (BRICK-42~44 — 설계서 상한).
 * UI 입력 범위 + 서버 clamp 상한이 이 상수를 공유한다. 플레이리스트·이벤트
 * 스케줄 양쪽이 같은 값을 써야 한다(scheduleRepo 참조).
 */
export const TRANSITION_MIN_MS = 200;
export const TRANSITION_MAX_MS = 9999;
export const TRANSITION_DEFAULT_MS = 1000;

export type Playlist = ContentAccessMetadata & {
  id: string;
  name: string;
  /** Library folder this playlist belongs to. null/undefined = ungrouped. */
  folderId?: string | null;
  /** Default canvas resolution (px). Playlists placed on a timeline render at
   *  this size. Defaults to 1920×1080. */
  resolutionWidth: number;
  resolutionHeight: number;
  /** Default display duration (sec) for still-image items. */
  imageDurationSec: number;
  /** Transition applied between consecutive items in this playlist. */
  transitionType: PlaylistTransitionType;
  /** Duration of the transition in milliseconds. Clamped to 0..TRANSITION_MAX_MS. */
  transitionDurationMs: number;
  items: PlaylistItem[];
  createdAt: string;
  updatedAt: string;
  /**
   * 승인본(id가 `cv-`로 시작)이면 그 원본 플레이리스트 id. 목록 API에는 실리지 않고 승인본을
   * 따로 읽을 때만 붙는다 — 화면이 같은 이름의 원본과 승인본을 하나로 보여 주는 데 쓴다(2026-10-01).
   */
  approvedFrom?: string;
};

/** Web video provider for `sourceKind === 'webUrl'` items. */
export type WebVideoProvider = 'youtube' | 'vimeo' | 'mp4';

export type MediaFitMode = 'contain' | 'cover' | 'stretch';
export const MEDIA_FIT_OPTIONS: ReadonlyArray<{ value: MediaFitMode; label: string; description: string }> = [
  { value: 'contain', label: '맞춤 (Contain)', description: '비율 유지 · 전체 표시' },
  { value: 'cover', label: '채우기 (Cover)', description: '비율 유지 · 일부 잘림' },
  { value: 'stretch', label: '늘이기 (Fill)', description: '비율 변경 · 화면 채움' },
];

/** Metadata captured at validate-url time for a web video. */
export type WebVideoMeta = {
  /** Display title shown in admin (oEmbed title for YT/Vimeo, filename for mp4). */
  title?: string;
  /** Thumbnail URL for admin preview. */
  thumbnailUrl?: string;
  /** Auto-extracted duration; operator may override. */
  durationSec?: number;
  /** Normalized embed URL — `https://www.youtube.com/embed/<id>` for YT,
   *  Vimeo player URL, or direct mp4 URL. Used by the player. */
  embedUrl?: string;
  /** Provider-specific video id (YouTube/Vimeo). */
  externalId?: string;
  /** True when the remote sends `Access-Control-Allow-Origin: *` and can be
   *  drawn into a WebGL texture for compositing. False = overlay-only. */
  corsAllowed?: boolean;
  /** Populated by the background download worker once the video has been
   *  cached locally as a MediaItem. When set, the player switches to the
   *  WebGL texture path (full warp applied) instead of the DOM overlay. */
  localMediaId?: string;
};

export type PlaylistItem = {
  /**
   * `'media'` → references a MediaItem or Notice via mediaId / noticeId.
   * `'webUrl'` → external web video (YouTube/Vimeo/direct mp4).
   * Default `'media'` for legacy rows.
   */
  sourceKind: 'media' | 'webUrl';
  /** Per-item media placement; missing media values use contain. Slides use noticeFitMode. */
  fitMode: MediaFitMode;
  /** Slide-only override; null/missing follows the Notice's authored fit mode. */
  noticeFitMode?: MediaFitMode | null;
  /**
   * For `sourceKind === 'media'`: exactly one of mediaId / noticeId is set.
   * For `sourceKind === 'webUrl'`: both are null and webUrl is set.
   */
  mediaId: string | null;
  noticeId: string | null;
  orderIndex: number;
  /**
   * Per-item override (sec) for still-image / notice display duration. When
   * null or omitted, the playlist's `imageDurationSec` default is used.
   * Videos ignore this — they play for their native length.
   * For webUrl items, this acts as the hard-timeout fallback when auto-extracted
   * duration is missing or unreliable.
   */
  durationSec: number | null;
  /** Original URL entered by the operator. Set when sourceKind === 'webUrl'. */
  webUrl: string | null;
  /** Provider detected at validation time. */
  webProvider: WebVideoProvider | null;
  /** Cached metadata from validate-url. Operator-edited durationSec lives here. */
  webMeta: WebVideoMeta | null;
};

// -----------------------------------------------------------------------------
// Notices — composable slides (text + image + video layers).
// Rendered client-side by compositing each layer onto an offscreen canvas per
// frame, which the Player then samples through the warp renderer.
// -----------------------------------------------------------------------------

/** Normalized artboard region. Editor metadata also defines the playback clip. */
export type NoticeGridRegion = {
  preset: string;
  index: number;
  role: 'media' | 'overlay';
  x: number; y: number; width: number; height: number;
};

export type NoticeLayerBase = {
  /** Editor-only, single-level group. Playback still uses ordinary layer geometry. */
  editorGroup?: { id: string; name: string };
  gridCell?: NoticeGridRegion;
  id: string;
  /** Optional human-readable label shown in the editor's layer list. */
  name?: string;
  /** Pixel coordinates within the notice's virtual canvas (canvasWidth/Height). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Stacking order — higher draws on top. */
  z: number;
  opacity: number;
  rotation: number; // degrees, future-proofing (0 for MVP)
  /** Editor visibility toggle (eye icon). When true the layer is hidden on the
   *  canvas AND skipped by the player. Persisted inside the layers JSON, so no
   *  DB migration is needed. Absent = visible. */
  hidden?: boolean;
  /** Editor lock toggle (padlock icon). When true the layer cannot be moved,
   *  resized, or selected on the canvas (still editable via the layer list).
   *  Absent = unlocked. Player ignores this. */
  locked?: boolean;
  /** Optional visibility window within the scene timeline (seconds from scene
   *  start). Absent = visible for the whole scene. endSec null = until scene
   *  end. Used by the player to gate per-layer appearance/disappearance. */
  timeline?: {
    startSec: number;
    endSec: number | null;
  };
};

export type NoticeFontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

/** Corner radius in canvas pixels — one value for every corner or [topLeft, topRight,
 *  bottomRight, bottomLeft]. Absent/0 = square. Clips images and videos, rounds text
 *  backgrounds. Added for the Station C Figma templates (card image, NEWS pill). */
export type NoticeCornerRadius = number | readonly [number, number, number, number];

export type NoticeTextLayer = NoticeLayerBase & {
  kind: 'text';
  text: string;
  fontSize: number; // pixels, in virtual canvas space
  fontWeight: NoticeFontWeight;
  color: string; // CSS color
  backgroundColor: string | null; // null = transparent
  align: 'left' | 'center' | 'right';
  /** Vertical alignment of the text within the layer box. Absent = 'middle'
   *  (matches the pre-field render). Persisted in the layers JSON. */
  verticalAlign?: 'top' | 'middle' | 'bottom';
  /** Optional CSS font-family. Absent = app default (Inter Tight / Pretendard).
   *  Persisted in the layers JSON. */
  fontFamily?: string;
  /** Line height as a multiple of fontSize. Absent = 1.2 (the historical player value). */
  lineHeight?: number;
  /** Letter spacing in canvas pixels (CSS letter-spacing). Absent = 0. */
  letterSpacing?: number;
  cornerRadius?: NoticeCornerRadius;
  /** Optional line padding in pixels inside the background box. */
  paddingX: number;
  paddingY: number;
};

/** Region-search result. Only name and coordinates are persisted in a weather source. */
export type WeatherLocationOption = {
  id: string;
  placeName: string;
  detail: string;
  latitude: number;
  longitude: number;
};

export type NoticeImageLayer = NoticeLayerBase & {
  kind: 'image';
  mediaId: string;
  /** How the image fits its bounding box. */
  fit: MediaFitMode;
  mediaScale?: number;
  mediaOffsetX?: number;
  mediaOffsetY?: number;
  cornerRadius?: NoticeCornerRadius;
};

/** Playback style for time-based layers within their visibility window.
 *  loop = repeat continuously (legacy `loop:true`), once = play through then
 *  hide, hold = play through then freeze on the last frame. */
export type LayerRepeat = 'loop' | 'once' | 'hold';

export type NoticeVideoLayer = NoticeLayerBase & {
  kind: 'video';
  mediaId: string;
  fit: MediaFitMode;
  mediaScale?: number;
  mediaOffsetX?: number;
  mediaOffsetY?: number;
  cornerRadius?: NoticeCornerRadius;
  loop: boolean;
  muted: boolean;
  /** Overrides `loop` when set. Falls back to loop?'loop':'once' if absent. */
  repeat?: LayerRepeat;
};

export type NoticeLottieLayer = NoticeLayerBase & {
  kind: 'lottie';
  mediaId: string;
  loop: boolean;
  autoplay: boolean;
  repeat?: LayerRepeat;
};

/**
 * Slot-based template layer that renders the "BRICK BRIEF" design as a single
 * composite layer. Visually fixed (background grid, dark card, lime/pink
 * accents, six 5-layer logo shadows, sticker illustrations) — only the six
 * text slots vary per Notice. Maps 1:1 to the ParsedNotice shape so the chat
 * poller can populate slots without a separate transformer.
 *
 * Coordinates default to (0, 0, 1920, 1080) — the template is built around
 * the full virtual canvas. Custom x/y/w/h are still respected if the editor
 * resizes/repositions it (e.g. as a corner watermark).
 */
/**
 * Optional dynamic source for a BRICK BRIEF layer. When omitted (or `kind:
 * 'static'`), the layer renders the manually-edited `slots` verbatim — the
 * 0.2.x baseline. When `kind: 'chatRotation'`, the Player ignores the
 * baked-in slots and instead cycles through the chat-ingest "rotation pool"
 * (see `GET /api/integrations/chat/rotation-pool`), updating the BRICK BRIEF
 * slots from each ParsedNotice in turn. The static slots remain as a
 * placeholder used when the pool is empty.
 */
export type NoticeBrickBriefSource =
  | { kind: 'static' }
  | { kind: 'dummy' }
  | {
      kind: 'chatRotation';
      /** Milliseconds between rotation steps. Default 10000. */
      intervalMs: number;
      /** Drop messages whose ingestedAt is older than this many months. Default 5. */
      maxAgeMonths: number;
      /** Statuses to include. Default includes every status so live previews
       *  can show recent real data even after an event has passed. */
      includeStatuses: NoticeStatus[];
      /** When null, every enabled chat integration contributes. When an
       *  explicit array, only the listed ChatIntegration ids are pulled. */
      chatIntegrationIds: string[] | null;
    };

export type NoticeBrickBriefLayer = NoticeLayerBase & {
  kind: 'brickBrief';
  slots: {
    /** Large dark-card header line. ≤24 chars recommended. */
    header: string;
    /** "제목" row value. ≤40 chars recommended. */
    title: string;
    /** "날짜" row value (e.g. "2026년 5월 16일"). */
    date: string;
    /** "시간" row value (e.g. "15:30~16:10"). */
    time: string;
    /** "장소" row value. */
    location: string;
    /** Multi-line body, rendered in pink. `\n` preserved. */
    content: string;
  };
  /** Optional dynamic source — see {@link NoticeBrickBriefSource}. Omitted
   *  on legacy rows; treat as `{ kind: 'static' }`. */
  source?: NoticeBrickBriefSource;
  /** Legacy visual skin marker. BRICK BRIEF now renders the data card only;
   *  omitted legacy values are treated the same as `data`. */
  variant?: 'data';
};

/**
 * Live "room status" board. One layer renders every registered (and enabled)
 * meeting-room calendar in a single composite — a shared time axis on top
 * and one timetable row per room beneath. The Player renderer reads cached
 * `calendar_events` for each linked integration and draws a fixed-window
 * board (default 08:00–20:00 local time, 12 h) with a NOW marker spanning
 * all rows.
 *
 * Coordinates default to the full virtual canvas (0, 0, 1920, 1080) so a
 * single-layer Notice renders edge-to-edge.
 */
export type NoticeRoomStatusLayer = NoticeLayerBase & {
  kind: 'roomStatus';
  /** CalendarIntegration ids to include, in display order. When `null`, the
   *  Player auto-includes every currently-enabled room (the default policy).
   *  Operators can pin a specific subset via the Admin Rooms editor. */
  roomIntegrationIds: string[] | null;
  /** Window start hour, 0–23. Default 8. */
  windowStartHour: number;
  /** Window end hour, 0–23 (exclusive). Default 20. */
  windowEndHour: number;
  /** Layout orientation. `vertical` puts the time axis on the left and one
   *  room per column (better for tall single-wall outputs like corner
   *  projection); `horizontal` puts time on top with one room per row. */
  orientation?: 'horizontal' | 'vertical';
};

/**
 * Per-room calendar card. Renders a single meeting room's "today" event list
 * (up to 5 cards) using a 4-city themed template. Unlike `roomStatus` which
 * fits an N-room timetable into a single composite, `roomCard` is one card
 * per room — operators place multiple `roomCard` layers (or mix with
 * `roomStatus`) inside the same Notice.
 *
 * Data flow: the Player frame loop already maintains a single
 * eventsByRoomId Map (shared with `roomStatus`); the compositor picks
 * events by `calendarIntegrationId`. No new poller required.
 */
export type NoticeRoomCardLayer = NoticeLayerBase & {
  kind: 'roomCard';
  /** CalendarIntegration id this card binds to. Empty string until the
   *  operator picks one — the Admin save button stays disabled while empty
   *  so the persisted form is always a valid non-empty id. */
  calendarIntegrationId: string;
  /** Visual theme keyed to one of the four city designs. */
  theme: RoomCardTheme;
  /** Header text. `null` → auto-generated from the linked room's label/name
   *  (see `resolveRoomCardTitle` in the compositor). */
  title: string | null;
  /** When true, the bottom-right wordmark slot renders. */
  showLogo: boolean;
};

export type RoomTemplateDataMode = 'dummy' | 'live';

export type NoticeRoomTemplateLayer = NoticeLayerBase & {
  kind: 'roomTemplate';
  theme: RoomCardTheme;
  dataMode: RoomTemplateDataMode;
  /** Required only when dataMode is `live`. */
  calendarIntegrationId: string | null;
};

/**
 * Single source of truth for the supported room-card themes. Adding a new
 * city: append to this array + add an entry to `ROOM_CARD_THEME_TOKENS` +
 * drop the matching `<key>-decoration.{png|json}` / `<key>-logo.{png|json}`
 * into `packages/player/src/assets/room-card/`. No conditional branches to
 * update anywhere else.
 */
export const ROOM_CARD_THEMES = [
  'tokyo',
  'hanoi',
  'helsinki',
  'hongkong',
] as const;
export type RoomCardTheme = (typeof ROOM_CARD_THEMES)[number];

/** Visual tokens consumed by both the Canvas2D compositor and the React
 *  preview component. Adding a token here requires updating every entry of
 *  `ROOM_CARD_THEME_TOKENS` — the `Record` type guarantees exhaustiveness. */
export type RoomCardThemeTokens = {
  /** Page background fill. */
  bg: string;
  /** Top-left header text color. */
  headerText: string;
  /** Individual card body fill. */
  cardBg: string;
  /** Left-side time block fill (accent color per city). */
  timeBlock: string;
  /** Time block text (typically high-contrast on `timeBlock`). */
  timeBlockText: string;
  /** Card title text. */
  cardText: string;
  /** Asset filename prefix — `<assetKey>-decoration.{png|json}` and
   *  `<assetKey>-logo.{png|json}` are looked up in `assets/room-card/`. */
  assetKey: string;
};

export type RoomTemplateCardLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
  gap: number;
  radius: number;
  timeWidth: number;
  borderWidth: number;
  borderColor: string;
  cardBg: string;
  timeBg: string;
  timeText: string;
  titleText: string;
  timeFontSize: number;
  titleFontSize: number;
  timeFontWeight: 400 | 500 | 600 | 700;
  titleFontWeight: 400 | 500 | 600 | 700;
  titleOffsetX: number;
  maxCards: number;
};

export const ROOM_TEMPLATE_CARD_LAYOUTS: Record<RoomCardTheme, RoomTemplateCardLayout> = {
  tokyo: {
    x: 64,
    y: 259,
    width: 842,
    height: 133,
    gap: 20,
    radius: 15,
    timeWidth: 240,
    borderWidth: 3,
    borderColor: '#1350A3',
    cardBg: '#FFFFFF',
    timeBg: '#FFF157',
    timeText: '#00182F',
    titleText: '#1A1A1A',
    timeFontSize: 33,
    titleFontSize: 32,
    timeFontWeight: 700,
    titleFontWeight: 700,
    titleOffsetX: 34,
    maxCards: 5,
  },
  hanoi: {
    x: 64,
    y: 259,
    width: 842,
    height: 133,
    gap: 20,
    radius: 15,
    timeWidth: 240,
    borderWidth: 3,
    borderColor: '#1350A3',
    cardBg: '#FCF4DF',
    timeBg: '#6BB6D4',
    timeText: '#00182F',
    titleText: '#00182F',
    timeFontSize: 33,
    titleFontSize: 32,
    timeFontWeight: 700,
    titleFontWeight: 700,
    titleOffsetX: 34,
    maxCards: 5,
  },
  helsinki: {
    x: 64,
    y: 259,
    width: 842,
    height: 133,
    gap: 20,
    radius: 15,
    timeWidth: 240,
    borderWidth: 3,
    borderColor: '#1350A3',
    cardBg: '#FFFFFF',
    timeBg: '#FF5532',
    timeText: '#00182F',
    titleText: '#003580',
    timeFontSize: 33,
    titleFontSize: 32,
    timeFontWeight: 700,
    titleFontWeight: 700,
    titleOffsetX: 34,
    maxCards: 5,
  },
  hongkong: {
    x: 64,
    y: 259,
    width: 842,
    height: 133,
    gap: 20,
    radius: 15,
    timeWidth: 240,
    borderWidth: 3,
    borderColor: '#1350A3',
    cardBg: '#FFFFFF',
    timeBg: '#D87F21',
    timeText: '#00182F',
    titleText: '#1A1A1A',
    timeFontSize: 33,
    titleFontSize: 32,
    timeFontWeight: 700,
    titleFontWeight: 700,
    titleOffsetX: 34,
    maxCards: 5,
  },
};

/**
 * TODO(designer): color values are placeholder approximations of each city's
 * cultural palette — confirm against the Figma source (file
 * HjHc5E3PAq56a9KKBIGrhE, nodes 3415:14218 / 3415:15386 / 3419:16708 /
 * 3419:17965) and replace.
 */
export const ROOM_CARD_THEME_TOKENS: Record<RoomCardTheme, RoomCardThemeTokens> = {
  tokyo: {
    bg: '#FFB7C5',
    headerText: '#1A1A1A',
    cardBg: '#FFFFFF',
    timeBlock: '#E5306E',
    timeBlockText: '#FFFFFF',
    cardText: '#1A1A1A',
    assetKey: 'tokyo',
  },
  hanoi: {
    bg: '#FEA14E',
    headerText: '#00182F',
    cardBg: '#FCF4DF',
    timeBlock: '#6BB6D4',
    timeBlockText: '#00182F',
    cardText: '#00182F',
    assetKey: 'hanoi',
  },
  helsinki: {
    bg: '#F0F4F8',
    headerText: '#003580',
    cardBg: '#FFFFFF',
    timeBlock: '#003580',
    timeBlockText: '#FFFFFF',
    cardText: '#003580',
    assetKey: 'helsinki',
  },
  hongkong: {
    bg: '#DE2910',
    headerText: '#FFD500',
    cardBg: '#FFFFFF',
    timeBlock: '#1A1A1A',
    timeBlockText: '#FFD500',
    cardText: '#1A1A1A',
    assetKey: 'hongkong',
  },
};

export type NoticeDataWidgetSource =
  /** `part` splits a digital clock into separately placed hour / minute widgets, or a
   *  horizontal seconds tape (current second centred, neighbours fading, sliding right→left). */
  | { kind: 'clock'; mode?: 'digital' | 'analog'; part?: 'hour' | 'minute' | 'secondsTape'; hour12: boolean; showSeconds: boolean; showDate?: boolean; showWeekday?: boolean }
  /** `day` = two-digit day of month only ("04"); `weekday-day` = English weekday + day ("SUN 04")
   *  in one run so the gap follows the actual glyph width like a Figma auto-layout row.
   *  `month-day-weekday` = Korean month, day and short weekday in one run ("9월 27일 (일)"). */
  | { kind: 'date'; format: 'long' | 'numeric' | 'short' | 'day' | 'weekday-day' | 'month-day-weekday' }
  /** `en-short` = upper-case English abbreviation ("SUN"). */
  | { kind: 'weekday'; format: 'long' | 'short' | 'en-short' }
  | { kind: 'weather'; latitude: number; longitude: number; placeName: string; locationMode?: 'fixed' | 'device' }
  | { kind: 'rss'; url: string }
  /** 대한민국 정책브리핑 정책뉴스 (data.go.kr 15095335). Fixed server-side source: no URL, no key in the notice. */
  | { kind: 'policyNews' }
  /** 네이버 뉴스 검색 (NAVER API HUB). BO가 운영사 설정에 등록한 공용 키를 서버에서만 쓴다. 슬라이드에는 검색어·정렬만 저장. */
  | { kind: 'naverNews'; query: string; sort?: 'date' | 'sim' }
  | { kind: 'notices' }
  | { kind: 'schedule' };

/** Linear gradient used as the text fill of a data widget. `angle` follows the CSS
 *  convention (0 = to top, 90 = to right, 180 = to bottom); `start`/`end` are the 0–1
 *  positions of the two colour stops along that axis. */
export type NoticeTextGradient = { from: string; to: string; angle?: number; start?: number; end?: number };

/** Source settings are part of the immutable notice; only fetched text changes. */
export type NoticeDataWidgetLayer = NoticeLayerBase & {
  kind: 'dataWidget';
  source: NoticeDataWidgetSource;
  hideWhenUnavailable?: boolean;
  fontFamily?: string;
  /** CSS font weight 100–900. Absent = 500 for clocks, 400 otherwise. */
  fontWeight?: number;
  /** Optional gradient text fill; overrides `color` for glyphs (analog hands keep `color`). */
  textGradient?: NoticeTextGradient;
  align?: 'left' | 'center' | 'right';
  weatherLayout?: 'horizontal' | 'vertical';
  /** Absent = shown, except `condition` (상태 문구, added later) which is shown only when true. */
  weatherFields?: { place?: boolean; icon?: boolean; condition?: boolean; temperature?: boolean; humidity?: boolean; wind?: boolean };
  title: string;
  fontSize: number;
  color: string;
  backgroundColor: string | null;
};

export type NoticeShapeKind = 'rectangle' | 'ellipse' | 'line' | 'triangle' | 'speechBubble' | 'star' | 'polygon';

/** Vector shape. One SVG path (see noticeShape.ts) is drawn by the editor, thumbnail and Player. */
export type NoticeShapeLayer = NoticeLayerBase & {
  kind: 'shape';
  shape: NoticeShapeKind;
  /** CSS colour; null = no fill. Lines ignore it. */
  fill: string | null;
  /** CSS colour; null = no outline. */
  stroke: string | null;
  /** Outline width in canvas pixels, kept inside the box. */
  strokeWidth: number;
  /** Rectangle / speech bubble corner radius in canvas pixels. */
  cornerRadius?: number;
  /** Polygon sides or star points, 3–12. Absent = 6 (polygon) / 5 (star). */
  sides?: number;
  /** Line only: axis or box diagonal the line follows. Absent = horizontal. */
  lineDirection?: 'horizontal' | 'vertical' | 'down' | 'up';
};

export type NoticeLayer =
  | NoticeShapeLayer
  | NoticeDataWidgetLayer
  | NoticeTextLayer
  | NoticeImageLayer
  | NoticeVideoLayer
  | NoticeLottieLayer
  | NoticeBrickBriefLayer
  | NoticeRoomStatusLayer
  | NoticeRoomCardLayer
  | NoticeRoomTemplateLayer;

/** Default scene length (seconds) when a Notice has no explicit duration. */
export const DEFAULT_NOTICE_DURATION_SEC = 10;

export type Notice = ContentAccessMetadata & {
  id: string;
  name: string;
  /** Virtual canvas dimensions. Default 1920x1080. */
  canvasWidth: number;
  canvasHeight: number;
  /** Fit the authored slide to the device viewport. Legacy/missing values use contain. */
  fitMode?: MediaFitMode;
  /** Solid background fill painted behind all layers. null = none (transparent
   *  over the player's black base). Hex like '#FFFFFF'. */
  canvasFill: string | null;
  /** Fill opacity 0–100 (percent). Ignored when canvasFill is null. */
  canvasFillOpacity: number;
  /** Library folder this scene belongs to. null/undefined = ungrouped. */
  folderId?: string | null;
  /** Scene length in seconds — drives the per-layer timeline and the default
   *  display duration when this scene is used as a playlist item. */
  durationSec: number;
  layers: NoticeLayer[];
  /** Relative path of the rasterized preview PNG, null until first save. */
  thumbnailRelPath: string | null;
  /**
   * 기본 템플릿 플래그(Owner 관리). 기준 조직(org-default)에서 true인 슬라이드는 신규 조직에
   * 자동 복제되고, 해제하면 다른 조직의 복제본이 제거된다. 구 클라이언트 호환을 위해 optional.
   */
  isTemplate?: boolean;
  /** 이 슬라이드가 기본 템플릿에서 복제된 경우 원본 notice id. */
  seedSourceId?: string | null;
  createdAt: string;
  updatedAt: string;
};

// -----------------------------------------------------------------------------
// Schedule (minute-resolution, Asia/Seoul)
// -----------------------------------------------------------------------------

export type ScheduleTarget =
  | { kind: 'media'; mediaId: string }
  | { kind: 'playlist'; playlistId: string }
  | { kind: 'notice'; noticeId: string }
  | { kind: 'stop'; idle?: boolean };

/** Explicit organization-group destinations; device targets remain bound to their approved group. */
export type ScheduleDeliveryTarget =
  | { kind: 'group'; groupId: string }
  | { kind: 'devices'; groupId: string; deviceIds: string[] };

/**
 * Simplified recurrence model:
 *   - 'time-of-day': fires at hour:minute. Days-of-week are inherited from
 *     the Schedule's `scheduleTypeId` (when null → daily/every day).
 *   - 'once': one-shot at a specific ISO datetime.
 *
 * Legacy 'weekly'/'daily' kinds are auto-migrated on DB open.
 */
/**
 * Optional daily window end (time-of-day, Asia/Seoul) shared by both recurrence
 * kinds. When set (and after the start), the event behaves like a timeline
 * block: its target loops from start until this time, then playback returns to
 * the timeline. The scheduler evaluates the window level-triggered every tick,
 * so it resumes mid-window after a restart. When absent, `repeatCount` governs
 * how long the event plays (legacy point-in-time behavior). Persisted in the
 * recurrence JSON — no migration. Overnight windows (end ≤ start) are treated
 * as "no end".
 */
export type ScheduleRecurrence =
  | {
      kind: 'once'; // one specific day
      at: string; // ISO datetime, Asia/Seoul (date + start time)
      endHour?: number; // 0-23 — window end on that date
      endMinute?: number; // 0-59
    }
  | {
      kind: 'time-of-day';
      hour: number; // 0-23
      minute: number; // 0-59
      endHour?: number; // 0-23
      endMinute?: number; // 0-59
      /**
       * Weekdays this recurring event fires on (0=Sun..6=Sat). When present it
       * overrides the bound ScheduleType's weekdays (the event picks its own
       * days within its date range). Absent = inherit the ScheduleType (legacy).
       */
      weekdays?: number[];
    };

/** Optional approval metadata for group-scoped admin views; legacy objects omit it. */
export type SchedulingApprovalMetadata = {
  groupId?: string | null;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'cancelled';
  approvalRequestId?: string;
  pendingRequestId?: string;
};

export type DeliveryTermination = { kind: 'cycles'; count: number } | { kind: 'until'; at: string };
export type DeliverySpec = { kind: 'immediate' | 'emergency'; termination: DeliveryTermination };
export type ScheduleExecution = PlaybackExecution & {
  kind: DeliverySpec['kind']; activatedAt: string; sequence: number; expiresAt: string;
};

export type Schedule = SchedulingApprovalMetadata & {
  /** Server-authored integrated-delivery authority. Never accepted from API clients. */
  deliveryPriority?: 'group' | 'central' | 'override';
  deliveryOverrideIds?: string[];
  /** Planner lane. 'base' plays only when no event-layer schedule is active. Omitted = 'event'. */
  layer?: 'base' | 'event';
  /**
   * Server-authored assignment projection only. Carries the period/repeat rule so the
   * selector can decide each day exactly. Never accepted from API clients.
   */
  assignmentRule?: import('./scheduleAssignments.js').AssignmentRule;
  /** Pending calendar requests may only fill otherwise unassigned time. */
  assignmentPending?: boolean;
  /** Runtime projection: an explicit event assignment replaces legacy events for this slot only. */
  assignmentExclusions?: Array<{ deviceId: string; weekday: number }>;
  /** Explicit new delivery origin; existing events are never inferred from name or priority. */
  delivery?: DeliverySpec;
  /** Runtime-only per-device execution, attached after approval. */
  execution?: ScheduleExecution;
  id: string;
  name: string;
  enabled: boolean;
  recurrence: ScheduleRecurrence;
  target: ScheduleTarget;
  /**
   * ScheduleType this event is bound to. When recurrence.kind ==='time-of-day',
   * the event fires on the ScheduleType's weekdays. null → every day
   * (type-independent) OR used with recurrence.kind==='once' (weekdays N/A).
   */
  scheduleTypeId: string | null;
  /** Optional date window (inclusive). ISO date YYYY-MM-DD. */
  validFrom: string | null;
  validUntil: string | null;
  /** Higher wins when multiple schedules collide on the same minute. */
  priority: number;
  /**
   * Number of times the event's target plays before auto-returning to the
   * timeline. Default 1 = play once. ≥2 repeats that many times. Ignored
   * for 'stop' targets.
   */
  repeatCount: number;
  /** Transition applied when this event preempts current playback. */
  transitionType: PlaylistTransitionType;
  /** Transition duration in ms. Clamped 0..TRANSITION_MAX_MS. */
  transitionDurationMs: number;
  /** NBL_REQ_504 — device ids this schedule targets. null = all org devices. */
  targetDeviceIds: string[] | null;
  /**
   * NBL_REQ_510 — 초안→발행. When false the schedule is a draft: it is saved
   * and editable but the engine never fires it (no effect on playback) until
   * it is published. Defaults true so legacy schedules stay live.
   */
  published: boolean;
  /**
   * Reviewer note set on reject. null = never rejected. A draft
   * (published=false) with a note is "반려", without a note is "승인 대기".
   */
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
};

// -----------------------------------------------------------------------------
// Timeline-based scheduling (daily block layout)
// -----------------------------------------------------------------------------

export type ScheduleType = SchedulingApprovalMetadata & {
  id: string;
  name: string;
  /** 0=Sun ... 6=Sat. Active on these weekdays. Empty + unbound = reusable, non-playing 24h template. */
  weekdays: number[];
  /** Display color for the type tab (not used per-block). */
  color: string;
  /** Soft-delete / suspension flag. */
  enabled: boolean;
  /** Protected types (평일/주말 seed) cannot be deleted via UI. */
  builtin: boolean;
  /**
   * Bound device id (NBL W6 — 타임라인). Reassignable from the 실시간 타임라인
   * device dropdown (NBL W12 — 디바이스 재배정 허용); the server keeps
   * device.assigned in sync on change. null = 디바이스 미지정 (also the default
   * for builtin template types 평일/주말).
   */
  deviceId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TimelineBlock = SchedulingApprovalMetadata & {
  id: string;
  scheduleTypeId: string;
  playlistId: string;
  /** Start minute-of-day, 0-1439 (00:00 = 0). */
  startMinute: number;
  /** End minute-of-day, 1-1440 (00:00 next day = 1440). Exclusive. */
  endMinute: number;
  color: string;
  label: string;
  /**
   * NBL_REQ_507 — 타임슬롯 위임. Optional block owner (a user id / email). When
   * set, the owner plus members holding a `timeline-block` resource grant may
   * swap this block's content (playlist) without org-wide content:write;
   * the time range and bound device stay owner/admin-only. null = no owner
   * (legacy: org-wide content:write governs the whole block).
   */
  ownerUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

// -----------------------------------------------------------------------------
// Change log (Phase 2 — offline sync support)
//
// Every mutation through the Repo layer appends one row to change_log. Player
// Sync Engine polls /api/sync/changes?since=<changeId> and applies the diff
// to its local SQLite mirror; on first connect (or whenever changeId is far
// behind) it falls back to /api/sync/snapshot.
// -----------------------------------------------------------------------------

export type ChangeLogEntityType =
  | 'media'
  | 'playlist'
  | 'schedule'
  | 'schedule-type'
  | 'timeline-block'
  | 'notice'
  | 'warp-preset'
  | 'app-state';

export type ChangeLogOp = 'upsert' | 'delete';

export type ChangeLogEntry = {
  /** Monotonically increasing INTEGER PRIMARY KEY AUTOINCREMENT. Player keeps
   *  the highest applied id as its sync cursor. */
  id: number;
  entityType: ChangeLogEntityType;
  entityId: string;
  op: ChangeLogOp;
  /** Full entity payload for upsert ops (so Player can apply without a
   *  follow-up fetch). null for delete. */
  payloadJson: string | null;
  createdAt: string;
};

/** Reply to GET /api/sync/snapshot — full state plus the change cursor the
 *  Player should resume from. Used on first connect or after change_log
 *  truncation. */
export type SyncSnapshot = {
  changeId: number;
  media: MediaItem[];
  playlists: Playlist[];
  schedules: Schedule[];
  scheduleTypes: ScheduleType[];
  timelineBlocks: TimelineBlock[];
  notices: Notice[];
  warpPresets: WarpPreset[];
};

/** Reply to GET /api/sync/changes?since=. Empty `entries` with the same head
 *  means the Player is caught up. */
export type SyncChanges = {
  /** Group scope revocation requires replacing the local authorized dataset. */
  resetRequired?: boolean;
  snapshot?: SyncSnapshot;
  head: number;
  entries: ChangeLogEntry[];
};

/** Reply to GET /api/media/:id/manifest — everything needed to fetch + verify
 *  the file without holding the response open for GBs of body. Player Sync
 *  Engine reads this before issuing the range-download. */
export type MediaManifest = {
  id: string;
  sizeBytes: number;
  sha256: string;
  mimeType: string;
  fileUrl: string;
  thumbnailUrl: string | null;
};

// -----------------------------------------------------------------------------
// Devices + pairing (Phase 3)
// -----------------------------------------------------------------------------

export type DeviceType = 'generic' | 'projector' | 'tv' | 'portrait';
export type DeviceOrientation = 'landscape' | 'portrait';
export type DeviceUpdateChannel = 'stable' | 'canary' | 'paused';

/** Player 가 heartbeat 로 보내는 콘텐츠 다운로드 진단. 원격에서 "왜 새 편성을 못 받는가"를 본다. */
export type DeviceDownloadStatus = {
  /** 이번 편성 파일 중 저장 완료 수 / 전체 수. */
  filesDone: number;
  filesTotal: number;
  /** 지금 받는 파일(없으면 생략). total 은 서버가 크기를 안 주면 null. */
  current?: { name: string; received: number; total: number | null };
  /** 가장 최근 다운로드·저장 실패. reason 은 'idle timeout', 'http 503', 'media integrity mismatch' 등. */
  lastFailure?: { name: string; reason: string; at: string };
  /** 편성 업데이트 연속 실패 횟수와 재시도 소진 여부(소진되면 WS 재연결까지 멈춘다). */
  updateFailures: number;
  retriesExhausted: boolean;
  /** 네트워크 없이 마지막 정상 편성(복원 편성)으로 재생 중. */
  bundleRestore: boolean;
  /** 현재 편성 파일 크기 합(바이트) — 원본(수 GB)인지 변환본(수백 MB)인지 원격에서 가른다(2026-10-07). */
  filesBytes?: number;
  /** 편성 파일 전체 검증이 마지막으로 성공한 시각. 60초 재확인이 도는지 보인다. */
  verifiedAt?: string;
  /** 복원 편성 캐시 버킷 수. 2 이상이면 옛 세대(원본 복사본)가 아직 안 지워진 것. */
  bundleBuckets?: number;
  /** 복원 편성 저장 상태 — 포인터가 가리키는 세대(format 1=원본 복사, 2=참조), 마지막 저장 시도와 결과, 지금 저장을 막는 조건. */
  bundle?: { format: number | null; gate?: string; lastSaveAt?: string; lastSaveResult?: string };
  /** 저장소가 실제로 어디에 쓰였는가 — Chrome `estimate().usageDetails`(caches/indexedDB…)와 Cache API 버킷별 항목 수·바이트. */
  storage?: { details?: Record<string, number>; buckets?: Array<{ name: string; entries: number; bytes: number }> };
  /** 마지막으로 오프라인 캐시 전체를 비운 시각과 호출 지점(`clearOfflineScope` reason). 등록 해지가 아닌데 비워졌는지 가린다. */
  lastScopeClear?: { at: string; reason: string };
  reportedAt: string;
};

export type DeviceNativeStatus = {
  remoteServerChange?: boolean; serverOrigin?: string; serverChangeStatus?: string;
  capturedAt?: string; sdkAvailable?: boolean; sdkVersion?: string; systemVersion?: string; serial?: string;
  /** Android shell >= 0.4.8: Build.MANUFACTURER / MODEL / DEVICE, own package and explicit runtime. */
  manufacturer?: string; model?: string; device?: string; packageName?: string; playerRuntime?: 'android_apk';
  timezone?: string; timezoneOffsetMinutes?: number; timezoneCorrection?: 'not-needed' | 'applied' | 'failed' | 'unsupported';
  rtcOffsetMs?: number; webViewVersion?: string; webBundleVersion?: string; webBundleSource?: string; launcherApp?: string; guardStatus?: string; updateStatus?: string; lastCrash?: string;
  /** Web layer (browser or APK shell): the `assets/index-*.js` it is running, whether it knows the
   *  server serves a different one, and whether the APK diagnostic trace pins the bundled shell. */
  webBundle?: string; webBundleStale?: boolean; diagnosticMode?: boolean;
  temperature?: string; cpuUsage?: string; gpuUsage?: string; memoryUsed?: number; memoryTotal?: number;
  storageFreeBytes?: number; storageTotalBytes?: number; cacheStorageLevel?: string; inputSource?: number; resolution?: string;
  backlight?: number; screenBright?: boolean; scheduleEnabled?: boolean; scheduledOffAt?: number; scheduledOnAt?: number;
  /**
   * Android shell >= 0.4.16. `canSelfRecover` is the overlay permission the
   * watchdog needs to bring the Player back; without it a covered panel stays
   * covered. `bootLocale` differs from `locale` until the panel restarts.
   */
  canSelfRecover?: boolean; isDefaultHome?: boolean; locale?: string; bootLocale?: string;
  /** Android shell >= 0.5.10: default-network transport and the current Wi-Fi association. */
  networkType?: 'wifi' | 'ethernet' | 'other' | 'none'; wifiSsid?: string; wifiRssi?: number; wifiLevel?: number;
  wifiLinkMbps?: number; wifiFrequencyMhz?: number;
  /** Android shell >= 0.5.11: Wi-Fi stability over the last hour, APs in range (strongest first, up to 20) and an operator BSSID pin. */
  wifiBssid?: string; wifiDisconnects1h?: number; wifiBandSwitches1h?: number;
  wifiAps?: DeviceWifiAp[]; wifiPin?: DeviceWifiPin;
  /** Android shell >= 0.5.13: whether the current network can be pinned, why not, and when the hourly counts started. */
  wifiPinMode?: DeviceWifiPinMode; wifiPinProbe?: string; wifiStatsSince?: string;
  /** Android shell >= 0.5.15: last 20 Wi-Fi drops, joins and band switches, oldest first. */
  wifiEvents?: DeviceWifiEvent[];
};

export type DeviceWifiEvent = { at: string; type: 'drop' | 'join' | 'band'; frequencyMhz?: number; rssi?: number };

/**
 * 0.5.14+: own = saved through the BRICK Lit Wi-Fi dialog, so it can be pinned · unsupported = saved in Android Settings.
 * Older shells also reported root (0.5.13) and open/stored/passphrase (0.5.12); none of those may pin.
 */
export type DeviceWifiPinMode = 'own' | 'root' | 'unsupported' | 'open' | 'stored' | 'passphrase';

/** One AP from the panel's last Wi-Fi scan; `ssid` is empty for hidden networks. */
export type DeviceWifiAp = { ssid: string; bssid: string; frequencyMhz: number; rssi: number };
export type DeviceWifiPin = { bssid: string; state: 'applying' | 'applied' | 'rolled_back' | 'failed' | 'cleared'; message?: string; at?: string };

export type Device = {
  id: string;
  name: string;
  siteLabel: string | null;
  createdAt: string;
  pairedAt: string | null;
  revokedAt: string | null;
  lastSeenAt: string | null;
  lastSyncChangeId: number | null;
  cacheBytes: number | null;
  appVersion: string | null;
  // NBL_REQ_202/203 — device profile. Defaults keep pre-NBL devices valid.
  deviceType: DeviceType;
  /** null = adopt the source/scene canvas resolution. */
  resolutionW: number | null;
  resolutionH: number | null;
  orientation: DeviceOrientation;
  /** Free-text site/group bucket (NBL_REQ_205 precursor). */
  groupLabel: string | null;
  // NBL_REQ_206/704 — live monitoring.
  /** Label of the item the Player last reported rendering, or null. */
  nowPlaying: string | null;
  /** Derived (not stored): lastSeenAt within the heartbeat window. */
  online: boolean;
  // NBL_REQ_204 — per-device content assignment.
  /** Played when no schedule block is active. null = org default. */
  defaultPlaylistId: string | null;
  /** Schedule type this device follows. null = unbound. */
  assignedScheduleTypeId: string | null;
  /** 마지막 수정 시각 — 프로필이 한 번도 안 바뀌었으면 null (BRICK-153). */
  updatedAt: string | null;
  /** 마지막으로 수정한 관리자 이메일. 자동 변경이면 null. */
  updatedBy: string | null;
  // SCDS — 전원 스케줄. 패널 펌웨어는 다음 1회분 off/on 슬롯 하나만 들고 있어
  // Player 가 부팅 직후와 하루 한 번 다시 밀어 넣는다.
  /** null = 미설정. 기기 자체 주간 설정(오프라인 안전망)만 동작한다. */
  powerSchedule: DevicePowerSchedule | null;
  /** Player 가 패널에 스케줄을 마지막으로 적용했다고 보고한 시각. */
  powerAppliedAt: string | null;
  /** 백라이트가 꺼진 시각. 켜져 있으면 null. */
  screenOffAt: string | null;
  /** 운영시간 안에서 재생할 편성이 없을 때의 동작. */
  idleBehavior: IdleBehavior;
  /**
   * Player 가 보고한 패널 전원 제어 지원 여부. null 이면 아직 보고가 없어 모른다.
   * 벤더 SDK 가 응답하는 기기(Hikvision 사이니지)만 true 가 된다.
   */
  powerCapable: boolean | null;
  updateChannel?: DeviceUpdateChannel;
  desiredAppVersion?: string | null;
  nativeStatus?: DeviceNativeStatus | null;
  nativeReportedAt?: string | null;
  /** Player 콘텐츠 다운로드 진단(2026-10-07). 보고 전/구버전 Player 는 null. */
  downloadStatus?: DeviceDownloadStatus | null;
  /** 이 기기에만 붙인 대기 화면이 저장돼 있는가(2026-09-18). 내용은 group-workflow/idle-content/device/:id 로 읽는다. */
  hasIdleNotice?: boolean;
  /** Execution environment + hardware identity (web / Android APK / HIKVISION APK). Absent on old snapshots. */
  runtime?: import('./deviceRuntime.js').DeviceRuntimeProfile | null;
};

/** BO(최고 관리자) 전조직 디바이스 목록 행 — 조직 컬럼/필터용. */
export type DeviceWithOrg = Device & {
  orgId: string;
  orgName: string;
};

/** Heartbeats older than this mark a device offline (NBL_REQ_206). */
export const DEVICE_OFFLINE_THRESHOLD_MS = 90_000;

/**
 * 기기 온라인 판정 — 서버·화면·진단이 모두 이 함수 하나만 쓴다.
 *
 * 왜 "마지막 신호 이후 경과 시간" 하나로만 재는가:
 *   • 기기가 살아 있다고 알릴 수 있는 방법은 자기 발로 신호를 보내는 것뿐이다. Player 는
 *     {@link DEVICE_OFFLINE_THRESHOLD_MS} 의 1/3 인 30초마다 하트비트를 보내므로, 한두 번
 *     빠뜨려도(네트워크 순간 끊김) 화면이 깜빡이지 않고 세 번 연속 놓쳐야 오프라인이 된다.
 *     90초는 그 여유(3비트)를 주면서도 운영자가 "꺼진 화면"을 1분 반 안에 알아채는 값이다.
 *   • WebSocket 연결 존재 여부로는 판정하지 않는다. 웹브라우저 Player 는 탭이 닫히거나
 *     기기 전원이 끊기면 종료 신호를 못 보내고(close 프레임 없이 사라짐), 반대로 프록시가
 *     연결만 붙잡고 있는 경우도 있어 "연결이 있다 = 살아 있다" 가 성립하지 않는다.
 *     신호가 끊기면 시간이 알아서 내려주는 쪽이 웹·네이티브(APK) 모두에 똑같이 동작한다.
 *   • 그래서 판정식을 여기 한 곳에 둔다. 예전에는 DeviceRepo 가 `< 임계값`, 진단 서비스가
 *     `> 임계값` 으로 각자 계산해 경계에서 한쪽만 온라인으로 보이는 어긋남이 있었다.
 *
 * @param lastSeenAt 기기가 마지막으로 보낸 신호 시각(ISO). null·파싱 불가면 오프라인.
 * @param now 판정 기준 시각(ms). 테스트에서 시계를 고정하려고 받는다.
 */
export function isDeviceOnline(lastSeenAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!lastSeenAt) return false;
  const seen = Date.parse(lastSeenAt);
  if (!Number.isFinite(seen)) return false;
  return now - seen < DEVICE_OFFLINE_THRESHOLD_MS;
}

/**
 * Display metrics a Player reports about the physical screen it's rendering on
 * (NBL_REQ_202/203 — resolution auto-acquisition, browser screen-API path).
 *
 * Computed Player-side as:
 *   screenW = Math.round(screen.width  * devicePixelRatio)
 *   screenH = Math.round(screen.height * devicePixelRatio)
 *   orientation = screen.orientation?.type starts with 'portrait' ? 'portrait' : 'landscape'
 *
 * The server uses these to auto-fill device.resolution_w/h/orientation ONLY
 * when those columns are still null — an operator's manual override is never
 * clobbered. All fields are optional so older Players keep working unchanged.
 */
export type DeviceDisplayMetrics = {
  screenW?: number;
  screenH?: number;
  orientation?: DeviceOrientation;
};

/** Reply to POST /api/devices/pair — given to the operator exactly once. */
export type PairingCodeIssued = {
  /** UUID for the pairing code row. */
  id: string;
  /** Raw human-friendly code (e.g. 'BRKL-7H2K-9XQR'). Operator types this
   *  into the Player's pairing dialog. Never persisted in raw form. */
  code: string;
  expiresAt: string;
  deviceName: string;
  siteLabel: string | null;
};

/** Reply to POST /api/devices/register (Player consumes pairing code). */
export type DeviceRegistrationResult = {
  device: Device;
  /** Long-lived bearer token. Player stores it locally (encrypted via
   *  Electron safeStorage) and sends it on every subsequent API call. */
  deviceToken: string;
};

// -----------------------------------------------------------------------------
// Admin session (Phase 3.5 — Google OAuth login)
// -----------------------------------------------------------------------------

export type AdminSession = {
  authProvider?: 'legacy' | 'google' | 'password';
  id: string;
  userEmail: string;
  userName: string | null;
  pictureUrl: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  lastSeenAt: string | null;
};

/** Reply to GET /api/auth/me — shown in Admin UI top bar. */
/** 관리자 등급 (Figma 권한 다이어그램).
 *  h9_super = 최고 관리자(H9/BO 운영자) · fo_super = 최고 관리자(구독 조직 Owner)
 *  · fo_admin = 관리자(그 외). */
export type AdminRole = 'h9_super' | 'fo_super' | 'fo_admin';

export type AuthMe = {
  mustChangePassword?: boolean;
  email: string;
  name: string | null;
  pictureUrl: string | null;
  /** 관리자 등급 — 네비 노출·GNB 표기용. */
  adminRole: AdminRole;
  /** OAuth scopes the user has currently granted on top of the base
   *  openid/email/profile set. Used by the Admin UI to decide whether to
   *  show the "Connect Chat" / "Connect Calendar" buttons. */
  grantedScopes: string[];
  /** BO 운영자가 고객사를 대행해서 보는 중이면 채워진다(2026-09-15 A안). 상단 띠 표시용. */
  acting?: {
    sessionId: string;
    orgId: string;
    orgName: string;
    scope: 'read' | 'write';
    expiresAt: string;
  };
};

// -----------------------------------------------------------------------------
// Phase 6 — Google integrations
// -----------------------------------------------------------------------------

/** OAuth scope keys we care about beyond the base login set. */
export const GOOGLE_SCOPE = {
  chatReadonly: 'https://www.googleapis.com/auth/chat.messages.readonly',
  chatSpacesReadonly:
    'https://www.googleapis.com/auth/chat.spaces.readonly',
  calendarListReadonly:
    'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
  calendarEventsReadonly:
    'https://www.googleapis.com/auth/calendar.events.readonly',
  /** Umbrella read-only scope — covers calendarList + events on every
   *  calendar the user can see, including Workspace resource (회의실)
   *  calendars shared with them. Preferred over calendar.events.readonly
   *  when the operator may add new rooms post-install without re-prompting. */
  calendarReadonly: 'https://www.googleapis.com/auth/calendar.readonly',
} as const;

/** Single Chat space entry returned from /api/integrations/chat/available-spaces. */
export type ChatAvailableSpace = {
  /** Resource name `spaces/AAAA...` — pass the trailing portion (after the
   *  slash) to chat_integrations.spaceId. */
  name: string;
  /** Operator-friendly name (the space's `displayName`). */
  displayName: string;
  /** Type: SPACE, GROUP_CHAT, DIRECT_MESSAGE. We filter to SPACE in UI. */
  spaceType: string;
};

export type GoogleScopeKey = keyof typeof GOOGLE_SCOPE;

/** Chat polling configuration entry (per Space). */
export type ChatIntegration = {
  id: string;
  /** Owning organization (tenant). NBL_REQ_602 — every integration is scoped
   *  to an org so the poller and routes never cross tenant boundaries. */
  orgId: string;
  /** Google Chat Space ID — e.g. "AAAAxxxxxxx" (from chat.google.com URL). */
  spaceId: string;
  /** Operator-supplied friendly name. */
  label: string;
  /** Email of the user whose OAuth grant the poller uses. */
  ownerEmail: string;
  enabled: boolean;
  /** Regex pattern used to filter qualifying messages. Defaults to the
   *  bracket-prefix patterns (공지/공지사항). */
  filterPattern: string;
  /** Display duration (seconds) for the auto-generated Notice in the
   *  active timeline. Defaults to 60. */
  displayDurationSec: number;
  createdAt: string;
  updatedAt: string;
  /** Read-only summary fields the API populates for the Admin UI. */
  lastPolledAt: string | null;
  lastSeenMessageAt: string | null;
  lastError: string | null;
  ingestedCount: number;
  /** Approval Chat spaces that receive draft cards. Empty means do not send. */
  approvalSpaceIds: string[];
  /** Emails allowed to confirm draft cards. */
  operatorEmails: string[];
  /** Reserved for follow-up auto-broadcast work. MVP keeps this off by default. */
  autoBroadcast: boolean;
  /**
   * Phase 6.10 — how messages enter the draft pipeline for this space.
   *  - 'poll'    : server polls the Chat space every tick (default, legacy).
   *  - 'mention' : the space is a confirm room; an @mention of the bot pushes
   *                a MESSAGE webhook that creates a draft instantly. The
   *                poller SKIPS these rooms (no polling), and the approval
   *                card is posted back to the same room.
   *  - 'admin'   : the space is the admin command console; an @mention runs a
   *                read-only command (schedule/event/device/notice queries).
   *                Poller skips it; no notice drafts are created here.
   */
  triggerMode: 'poll' | 'mention' | 'admin';
};

export type NoticeDraftSetStatus =
  | 'needs_approval_space'
  | 'pending'
  | 'confirmed'
  | 'failed';

export type NoticeDraftVariant = '간결' | '친근' | '공식';

export type NoticeDraft = {
  id: string;
  setId: string;
  variant: NoticeDraftVariant;
  templateId: string;
  header: string;
  subtitle: string;
  content: string;
  metaDate: string | null;
  metaTime: string | null;
  metaLocation: string | null;
  eventStart: string | null;
  eventEnd: string | null;
};

export type NoticeDraftPostStatus = 'active' | 'confirmed' | 'patch_failed';

export type NoticeDraftPost = {
  id: string;
  setId: string;
  approvalSpaceId: string;
  chatMessageName: string;
  status: NoticeDraftPostStatus;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type NoticeDraftSet = {
  id: string;
  sourceMsgId: string;
  integrationId: string;
  status: NoticeDraftSetStatus;
  confirmedDraft: string | null;
  confirmedBy: string | null;
  confirmedNotice: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  drafts: NoticeDraft[];
  posts: NoticeDraftPost[];
};

/** Time-window classification of a notice relative to "now".
 *
 *   upcoming — eventEndDate is in the future (event hasn't passed yet)
 *   ongoing  — now ∈ [eventStartDate, eventEndDate] (currently active)
 *   expired  — eventEndDate is before now (event already over)
 *   unknown  — no time information could be extracted
 *
 *  Always computed by deterministic code (now vs eventEndDate), never by the
 *  LLM directly — small models hallucinate at date arithmetic. The LLM only
 *  extracts ISO dates; we do the comparison. */
export type NoticeStatus = 'upcoming' | 'ongoing' | 'expired' | 'unknown';

/** Operator-visible metadata extracted from the body. Each field stores
 *  the verbatim phrase from the message ("2026.01.06", "점심시간 전까지",
 *  "우측 공용 냉장고") and is null when the body didn't mention that
 *  facet. The Player template renders a [META DATA] block only when at
 *  least one field is non-null. */
export type ParsedMetaData = {
  /** Date phrase in source form (e.g. "2026.01.06", "5/20 (화)"). */
  date: string | null;
  /** Time / deadline phrase (e.g. "점심시간 전까지", "09:00", "~17시까지"). */
  time: string | null;
  /** Place phrase (e.g. "우측 공용 냉장고", "본사 7층 Town Hall"). */
  location: string | null;
};

/** Structured notice extracted from a chat message by the local LLM
 *  (Ollama / qwen3:4b in Phase 6.7+). Length limits are enforced both by
 *  the prompt and by post-processing (truncation as backup).
 *
 *  6.7.2 — split out [META DATA] (date/time/location) and renamed
 *  title → header / body → content to match the operator's template
 *  vocabulary. The 0.2.x DB columns are kept under their old names
 *  (parsed_title, parsed_body, parsed_date) and mapped at the repo
 *  boundary so existing rows remain readable without migration. */
export type ParsedNotice = {
  /** Headline noun phrase, ≤24 chars. */
  header: string;
  /** Single-line context, ≤40 chars. */
  subtitle: string;
  /** Multi-line body. `\n` preserved so the Player template can render
   *  bullet-like lines. ≤200 chars total (post-process truncation). */
  content: string;
  /** Operator-visible metadata block. Always present as an object even
   *  when every facet is null — that way the UI can decide whether to
   *  hide the section based on a single `hasAnyMeta()` check. */
  metaData: ParsedMetaData;
  /** ISO date (YYYY-MM-DD). Translated from the message body using the
   *  message createTime as the year/month anchor. null when absent.
   *  Used by deterministic status computation, NOT by the UI render. */
  eventStartDate: string | null;
  eventEndDate: string | null;
  /** Code-computed status — see {@link NoticeStatus}. */
  status: NoticeStatus;
};

/** A single ingested chat message preserved for operator inspection.
 *  Joined with the created Notice (if any) so the Admin "최근 메시지"
 *  modal can show both the source text and the resulting Notice name. */
/**
 * Phase 6.9 — explicit confirmation lifecycle for ingested chat messages.
 * Replaces the binary `excluded` flag so the rotation pool can gate on
 * approval, and so deleted/disabled rows that get re-polled land back as
 * pending instead of silently re-appearing.
 *   - 'pending'   : ingested, awaiting operator confirmation
 *   - 'confirmed' : approved (via Chat card OR Admin notices tab) — visible
 *   - 'disabled'  : operator hid it, keep row for revival
 *   - 'deleted'   : operator soft-deleted it; re-poll resurfaces as pending
 */
export type ChatNoticeStatus = 'pending' | 'confirmed' | 'disabled' | 'deleted';

export type ChatRecentMessage = {
  /** The ChatIntegration this message was ingested under. Lets
   *  cross-integration views (e.g. the rotation pool) target the
   *  per-message exclusion endpoint without a separate lookup. */
  integrationId: string;
  /** Google Chat message resource name (e.g. spaces/XXX/messages/YYY). */
  messageId: string;
  /** Original message body as received from Chat. */
  messageText: string;
  /** Sender display name, when Chat returned one. */
  senderName: string | null;
  /** RFC3339 from Chat's `createTime`. May be null for legacy rows that
   *  predate the 6.6.1 schema widening. */
  messageCreateTime: string | null;
  /** When the poller ingested the message into our DB. */
  ingestedAt: string;
  /** The Notice that was created from this message, or null if ingestion
   *  failed before reaching the notice repo. */
  noticeId: string | null;
  /** Convenience field — the Notice's display name, joined at query time. */
  noticeName: string | null;
  /** LLM-parsed structured form. null for rows ingested before Phase 6.7
   *  or when Ollama was unavailable / parsing failed. */
  parsed: ParsedNotice | null;
  /** @deprecated Phase 6.9 — kept for backward compat with old Admin views.
   *  Derived as `noticeStatus !== 'confirmed'`. New code should read
   *  `noticeStatus` directly. */
  excluded: boolean;
  /** Phase 6.9 confirmation lifecycle. See {@link ChatNoticeStatus}. */
  noticeStatus: ChatNoticeStatus;
};

// -----------------------------------------------------------------------------
// Phase 6.6 — Google Calendar (meeting-room) integration
//
// Each tracked Google Workspace resource calendar (회의실) has one
// CalendarIntegration row. A worker polls every 60 s, fetching events in a
// ±12 h window around now via `calendarEvents.list`, and writes the result
// into calendar_events (replacing the prior window snapshot atomically).
// The Player renders the cached events through a future "회의실 상태"
// template — this layer only exposes the raw data.
// -----------------------------------------------------------------------------

/** Single calendar entry returned from /api/integrations/calendar/available-rooms.
 *  Mirrors the subset of Google Calendar API `CalendarListEntry` we care about. */
export type CalendarAvailableRoom = {
  /** The calendar id (use this as CalendarIntegration.calendarId). For
   *  Workspace resource calendars this looks like
   *  `<...>@resource.calendar.google.com`. */
  id: string;
  /** Operator-friendly name as Google stores it (e.g. "HNINE-10F-Hanoi 🇻🇳"). */
  summary: string;
  description: string | null;
  /** 'freeBusyReader' | 'reader' | 'writer' | 'owner' — we accept any. */
  accessRole: string;
  /** True for entries Google flags as resource (회의실) calendars. */
  isResource: boolean;
};

/** Per-room polling configuration. */
export type CalendarIntegration = {
  id: string;
  /** Owning organization (tenant). NBL_REQ_602 — calendar integrations are
   *  org-scoped; the poller derives the org from this field when caching events. */
  orgId: string;
  /** Google calendar id — used verbatim in API calls. */
  calendarId: string;
  /** Operator-supplied label shown in the Player template (e.g. "Hanoi"). */
  label: string;
  /** OAuth grant owner used by the poller. */
  ownerEmail: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  /** Read-only summary fields the API populates for the Admin UI. */
  lastPolledAt: string | null;
  lastError: string | null;
  /** Number of events currently cached for this calendar (the most recent
   *  successful poll's window). */
  eventCount: number;
};

/** A single cached event from a tracked calendar. Timestamps are stored as
 *  UTC unix milliseconds for portable range queries; the source RFC3339
 *  strings are preserved verbatim so the template can show the original
 *  timezone offset if needed. */
export type CalendarEvent = {
  /** Composite primary key — `${integrationId}:${googleEventId}` so the
   *  same Google event appearing in two rooms (rare) stays distinct. */
  id: string;
  integrationId: string;
  calendarId: string;
  /** Google event id, useful for de-dup if the template ever cross-links. */
  googleEventId: string;
  /** Event title. May be empty for sensitivity-tagged events. */
  summary: string;
  /** UTC ms. */
  startAt: number;
  endAt: number;
  /** RFC3339 strings as Google returned them (preserve original TZ offset). */
  startIso: string;
  endIso: string;
  /** Organizer email. The template decides whether to display it. */
  organizerEmail: string | null;
  /** `confirmed` | `tentative` | `cancelled`. We still store cancelled so
   *  the template can strike them through if desired. */
  status: 'confirmed' | 'tentative' | 'cancelled';
  /** Mirrors Google's `updated` field — used to detect changes between polls. */
  updatedAt: string;
};
